/**
 * Walk non-MathJax text nodes within containerEl, returning an ordered list
 * of { node, start, end } with character offsets into the concatenated visible text.
 */
function getTextNodes(containerEl) {
  const walker = document.createTreeWalker(containerEl, NodeFilter.SHOW_TEXT);
  const nodes = [];
  let offset = 0;
  let node;
  while ((node = walker.nextNode())) {
    if (node.parentElement?.closest("mjx-container, .MathJax, .MathJax_SVG"))
      continue;
    nodes.push({ node, start: offset, end: offset + node.textContent.length });
    offset += node.textContent.length;
  }
  return nodes;
}

/**
 * Returns the visible (non-MathJax) text content of containerEl.
 * @param {Element} containerEl
 * @returns {string}
 */
export function getFlatText(containerEl) {
  return getTextNodes(containerEl)
    .map(({ node }) => node.textContent)
    .join("");
}

/**
 * Map the start of a DOM Range to a character offset within getFlatText().
 * @param {Range} range
 * @param {Element} containerEl
 * @returns {number|null}
 */
export function getRangeStartOffset(range, containerEl) {
  for (const { node, start } of getTextNodes(containerEl)) {
    if (node === range.startContainer) return start + range.startOffset;
  }
  return null;
}

/**
 * Remove all highlight <mark> elements, unwrapping their children back into place.
 * @param {Element} containerEl
 */
export function clearHighlightsFromDOM(containerEl) {
  for (const mark of Array.from(
    containerEl.querySelectorAll("mark[data-highlight-id]"),
  )) {
    const parent = mark.parentNode;
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
    parent.removeChild(mark);
  }
  containerEl.normalize();
}

/** Length of the longest common substring of strings a and b. */
function lcsLen(a, b) {
  const la = a.length,
    lb = b.length;
  if (!la || !lb) return 0;
  let max = 0;
  const row = new Uint16Array(lb + 1);
  for (let i = 0; i < la; i++) {
    let prev = 0;
    for (let j = 0; j < lb; j++) {
      const tmp = row[j + 1];
      row[j + 1] = a[i] === b[j] ? prev + 1 : 0;
      if (row[j + 1] > max) max = row[j + 1];
      prev = tmp;
    }
  }
  return max;
}

function contextScore(flatText, idx, exact, prefix, suffix) {
  const pre = flatText.slice(Math.max(0, idx - prefix.length), idx);
  const suf = flatText.slice(
    idx + exact.length,
    idx + exact.length + suffix.length,
  );
  return lcsLen(pre, prefix) + lcsLen(suf, suffix);
}

/**
 * Find the best-matching start index for a text-quote anchor in flatText.
 *
 * Tries exact match first; if multiple hits, picks the one whose surrounding
 * prefix/suffix context is most similar (longest-common-substring scoring).
 * If no exact match, falls back to anchoring on the stored prefix or suffix.
 *
 * @param {string} flatText
 * @param {string} exact   - the selected text
 * @param {string} prefix  - up to 40 chars before the selection
 * @param {string} suffix  - up to 40 chars after the selection
 * @returns {number} start index, or -1 if no acceptable match
 */
export function findBestMatch(flatText, exact, prefix, suffix) {
  if (!exact || !flatText) return -1;

  const hits = [];
  let from = 0;
  for (;;) {
    const i = flatText.indexOf(exact, from);
    if (i === -1) break;
    hits.push(i);
    from = i + 1;
  }

  if (hits.length === 1) return hits[0];
  if (hits.length > 1) {
    let best = hits[0],
      bestScore = -1;
    for (const i of hits) {
      const s = contextScore(flatText, i, exact, prefix, suffix);
      if (s > bestScore) {
        bestScore = s;
        best = i;
      }
    }
    return best;
  }

  // No exact match — fuzzy: anchor on the tail of the stored prefix
  if (prefix.length >= 6) {
    const tail = prefix.slice(-Math.min(20, prefix.length));
    const pi = flatText.lastIndexOf(tail);
    if (pi !== -1) {
      const head = exact.slice(0, Math.min(12, exact.length));
      const hi = flatText.indexOf(head, pi + tail.length);
      if (hi !== -1 && hi < pi + tail.length + 80) return hi;
    }
  }

  // Fuzzy: anchor on the head of the stored suffix
  if (suffix.length >= 6) {
    const head = suffix.slice(0, Math.min(20, suffix.length));
    const si = flatText.indexOf(head);
    if (si !== -1) {
      const eTail = exact.slice(-Math.min(12, exact.length));
      const ti = flatText.lastIndexOf(eTail, si);
      if (ti !== -1 && ti > si - exact.length - 80) {
        return Math.max(0, ti - (exact.length - eTail.length));
      }
    }
  }

  return -1;
}

/**
 * Reconstruct the text of a DOM Range, replacing each mjx-container with its
 * LaTeX source (retrieved from MathJax's internal math list).
 *
 * MathJax 3 CHTML renders characters via CSS ::before pseudo-elements, so
 * sel.toString() returns empty for math.  This function walks the original DOM
 * and substitutes each equation with $...$ or $$...$$ instead.
 *
 * @param {Range}   range       — cloned Range representing the selection
 * @param {Element} containerEl — the paragraph element that owns the selection
 * @returns {string}
 */
export function captureSelectionWithLatex(range, containerEl) {
  // Map each mjx-container in this paragraph to its LaTeX source string
  const mathSources = new Map();
  if (window.MathJax?.startup?.document?.math) {
    for (const item of window.MathJax.startup.document.math) {
      if (item.typesetRoot && containerEl.contains(item.typesetRoot)) {
        const d = item.display ? "$$" : "$";
        mathSources.set(item.typesetRoot, `${d}${item.math}${d}`);
      }
    }
  }

  function extract(node) {
    if (!range.intersectsNode(node)) return "";

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      const start = node === range.startContainer ? range.startOffset : 0;
      const end = node === range.endContainer ? range.endOffset : text.length;
      return text.slice(start, end);
    }

    // mjx-container → LaTeX source (skip its CHTML/assistive children)
    if (node.nodeName.toLowerCase() === "mjx-container") {
      return mathSources.get(node) ?? "";
    }

    return Array.from(node.childNodes).map(extract).join("");
  }

  return extract(range.commonAncestorContainer);
}

/**
 * Build a highlight record from flat-text character offsets.
 * Used when creating trimmed or split pieces of an existing record.
 * @param {{ paragraphId: string, color: string }} base
 * @param {string} flat
 * @param {number} newStart
 * @param {number} newEnd
 */
export function makeHighlightRecord(base, flat, newStart, newEnd) {
  return {
    paragraphId: base.paragraphId,
    color: base.color,
    exact: flat.slice(newStart, newEnd),
    prefix: flat.slice(Math.max(0, newStart - 40), newStart),
    suffix: flat.slice(newEnd, newEnd + 40),
  };
}

/**
 * Inject a <mark> element into the DOM for one highlight record.
 * @param {Element} containerEl
 * @param {{ id: string, exact: string, prefix: string, suffix: string, color: string }} record
 * @param {(id: string) => void} [onRemove]
 * @returns {boolean} true if the mark was successfully inserted
 */
export function applyHighlightToDOM(containerEl, record, onRemove) {
  const { id, exact, prefix, suffix, color } = record;
  const flat = getFlatText(containerEl);
  const start = findBestMatch(flat, exact, prefix, suffix);
  if (start === -1) return false;

  const end = start + exact.length;
  const nodes = getTextNodes(containerEl);

  const startN = nodes.find((n) => n.start <= start && start < n.end);
  const endN = nodes.find((n) => n.start < end && end <= n.end);
  if (!startN || !endN) return false;

  const range = document.createRange();
  range.setStart(startN.node, start - startN.start);
  range.setEnd(endN.node, end - endN.start);

  const mark = document.createElement("mark");
  mark.className = `ct__highlight ct__highlight--${color}`;
  mark.dataset.highlightId = id;
  mark.title = "Click to remove highlight";
  if (onRemove)
    mark.addEventListener("click", (e) => {
      e.stopPropagation();
      onRemove(id);
    });

  try {
    range.surroundContents(mark);
  } catch {
    // Range straddles an element boundary — extract then re-insert
    try {
      mark.appendChild(range.extractContents());
      range.insertNode(mark);
    } catch {
      return false;
    }
  }
  return true;
}
